import React, {
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
  useToast,
} from "@chakra-ui/react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
} from "react-router-dom";

import {
  addProject,
  uploadProjectAttachment,
} from "./projectActions";

const MAX_FILE_SIZE =
  25 * 1024 * 1024;

const initialForm = {
  parish: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  pastor: "",
  projectTitle: "",
  projectDescription: "",
  seekingPermission: "",
  fundingSource: "",
  financeCouncil: "",
  layTrustees: "",
  attachments: [],
  additionalEmails: "",
};

const normalizeEmail = (email) =>
  typeof email === "string"
    ? email.trim().toLowerCase()
    : "";

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    normalizeEmail(email)
  );

const AddNewProject = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const user = useSelector(
    (state) => state.auth.user
  );

  const [form, setForm] =
    useState(initialForm);

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setForm((previousForm) => ({
      ...previousForm,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(
      event.target.files || []
    );

    const invalidFiles =
      selectedFiles.filter(
        (file) =>
          file.size <= 0 ||
          file.size >= MAX_FILE_SIZE
      );

    if (invalidFiles.length > 0) {
      toast({
        title:
          "One or more files cannot be uploaded",
        description:
          "Each file must contain data and be smaller than 25 MB.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      event.target.value = "";

      setForm((previousForm) => ({
        ...previousForm,
        attachments: [],
      }));

      return;
    }

    setForm((previousForm) => ({
      ...previousForm,
      attachments: selectedFiles,
    }));
  };

  const validateForm = () => {
    const requiredTextFields = [
      ["parish", "Parish/Institution"],
      [
        "contactName",
        "Contact Person's Full Name",
      ],
      [
        "contactPhone",
        "Contact Phone",
      ],
      [
        "contactEmail",
        "Contact Email",
      ],
      [
        "pastor",
        "Pastor/Administrator",
      ],
      [
        "projectTitle",
        "Project Title",
      ],
      [
        "projectDescription",
        "Project Description",
      ],
    ];

    for (const [
      fieldName,
      fieldLabel,
    ] of requiredTextFields) {
      if (!form[fieldName]?.trim()) {
        throw new Error(
          `${fieldLabel} is required.`
        );
      }
    }

    if (
      !isValidEmail(
        form.contactEmail
      )
    ) {
      throw new Error(
        "Enter a valid contact email address."
      );
    }

    if (!form.seekingPermission) {
      throw new Error(
        "Select the type of permission being requested."
      );
    }

    if (!form.fundingSource) {
      throw new Error(
        "Select a funding source."
      );
    }

    if (!form.financeCouncil) {
      throw new Error(
        "Select a Finance Council response."
      );
    }

    if (!form.layTrustees) {
      throw new Error(
        "Select a Lay Trustees response."
      );
    }
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      validateForm();

      const creatorEmail =
        normalizeEmail(user?.email);

      if (!creatorEmail) {
        throw new Error(
          "Your account email could not be verified. Please log out and try again."
        );
      }

      const contactEmail =
        normalizeEmail(
          form.contactEmail
        );

      const enteredAdditionalEmails =
        form.additionalEmails
          .split(",")
          .map(normalizeEmail)
          .filter(Boolean);

      const invalidAdditionalEmail =
        enteredAdditionalEmails.find(
          (email) =>
            !isValidEmail(email)
        );

      if (invalidAdditionalEmail) {
        throw new Error(
          `"${invalidAdditionalEmail}" is not a valid additional email address.`
        );
      }

      const additionalEmails = [
        ...new Set(
          enteredAdditionalEmails.filter(
            (email) =>
              email !== creatorEmail &&
              email !== contactEmail
          )
        ),
      ];

      const projectData = {
        parish: form.parish.trim(),
        contactName:
          form.contactName.trim(),
        contactPhone:
          form.contactPhone.trim(),
        contactEmail,
        pastor: form.pastor.trim(),
        projectTitle:
          form.projectTitle.trim(),
        projectDescription:
          form.projectDescription.trim(),
        seekingPermission:
          form.seekingPermission,
        fundingSource:
          form.fundingSource,
        financeCouncil:
          form.financeCouncil,
        layTrustees:
          form.layTrustees,
        additionalEmails,
        attachments: [],
        priority: "Normal",
        status: "Open",
        createdBy: {
          email: creatorEmail,
          firstName:
            user?.firstName ||
            user?.displayName ||
            "User",
        },
      };

      const projectReference =
        await dispatch(
          addProject(projectData)
        );

      const uploadResults =
        await Promise.allSettled(
          form.attachments.map(
            (file) =>
              uploadProjectAttachment(
                projectReference.id,
                file
              )
          )
        );

      const failedUploads =
        uploadResults.filter(
          (result) =>
            result.status ===
            "rejected"
        );

      setForm(initialForm);

      if (fileInputRef.current) {
        fileInputRef.current.value =
          "";
      }

      if (
        failedUploads.length > 0
      ) {
        toast({
          title: "Project submitted",
          description:
            `${failedUploads.length} attachment` +
            `${
              failedUploads.length === 1
                ? ""
                : "s"
            } could not be uploaded. ` +
            "You can add them from the project page.",
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Project submitted",
          description:
            "Your project and attachments were saved successfully.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }

      navigate(
        `/projects/${projectReference.id}`
      );
    } catch (error) {
      console.error(
        "Project submission failed:",
        error
      );

      toast({
        title:
          "Project could not be submitted",
        description:
          error.message ||
          "Please check the form and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      p={{
        base: 4,
        md: 6,
      }}
      maxW="800px"
      mx="auto"
      bg="white"
      borderRadius="md"
      boxShadow="md"
    >
      <Heading
        size="lg"
        mb={6}
      >
        Submit a New Project
      </Heading>

      <form onSubmit={handleSubmit}>
        <FormControl isRequired>
          <FormLabel>
            Parish/Institution
          </FormLabel>

          <Input
            name="parish"
            value={form.parish}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Contact Person&apos;s Full
            Name
          </FormLabel>

          <Input
            name="contactName"
            value={form.contactName}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Contact Phone
          </FormLabel>

          <Input
            name="contactPhone"
            type="tel"
            value={form.contactPhone}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Contact Email
          </FormLabel>

          <Input
            name="contactEmail"
            type="email"
            value={form.contactEmail}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Pastor/Administrator
          </FormLabel>

          <Input
            name="pastor"
            value={form.pastor}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Project Title
          </FormLabel>

          <Input
            name="projectTitle"
            value={form.projectTitle}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Project Description
          </FormLabel>

          <Textarea
            name="projectDescription"
            value={
              form.projectDescription
            }
            onChange={handleChange}
            minH="140px"
          />
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Seeking Permission For
          </FormLabel>

          <RadioGroup
            value={
              form.seekingPermission
            }
            onChange={(value) =>
              setForm(
                (previousForm) => ({
                  ...previousForm,
                  seekingPermission:
                    value,
                })
              )
            }
          >
            <Stack>
              <Radio value="Architectural / Engineering Design">
                Architectural /
                Engineering Design
              </Radio>

              <Radio value="Preliminary Approval">
                Preliminary Approval
              </Radio>

              <Radio value="Final Approval">
                Final Approval
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Funding Source
          </FormLabel>

          <RadioGroup
            value={form.fundingSource}
            onChange={(value) =>
              setForm(
                (previousForm) => ({
                  ...previousForm,
                  fundingSource:
                    value,
                })
              )
            }
          >
            <Stack>
              <Radio value="Revolving Fund">
                Revolving Fund
              </Radio>

              <Radio value="Catholic Strong">
                Catholic Strong
              </Radio>

              <Radio value="Donation">
                Donation
              </Radio>

              <Radio value="Restricted Gift">
                Restricted Gift
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Finance Council
          </FormLabel>

          <RadioGroup
            value={
              form.financeCouncil
            }
            onChange={(value) =>
              setForm(
                (previousForm) => ({
                  ...previousForm,
                  financeCouncil:
                    value,
                })
              )
            }
          >
            <Stack spacing={2}>
              {[
                "have approved",
                "have disapproved",
                "were not consulted",
              ].map((value) => (
                <Radio
                  key={value}
                  value={value}
                >
                  {value}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        <FormControl
          mt={4}
          isRequired
        >
          <FormLabel>
            Lay Trustees
          </FormLabel>

          <RadioGroup
            value={
              form.layTrustees
            }
            onChange={(value) =>
              setForm(
                (previousForm) => ({
                  ...previousForm,
                  layTrustees: value,
                })
              )
            }
          >
            <Stack spacing={2}>
              {[
                "have approved",
                "have disapproved",
                "were not consulted",
              ].map((value) => (
                <Radio
                  key={value}
                  value={value}
                >
                  {value}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        <FormControl mt={4}>
          <FormLabel>
            Additional Emails
          </FormLabel>

          <FormHelperText mb={2}>
            Separate multiple addresses
            with commas. Include the
            pastor&apos;s email if it is
            different from the contact
            email.
          </FormHelperText>

          <Input
            name="additionalEmails"
            value={
              form.additionalEmails
            }
            onChange={handleChange}
            placeholder="email1@example.com, email2@example.com"
          />
        </FormControl>

        <FormControl mt={4}>
          <FormLabel>
            Attachments
          </FormLabel>

          <FormHelperText mb={2}>
            Each file must be smaller
            than 25 MB.
          </FormHelperText>

          <Input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            onChange={handleFileChange}
            p={1}
          />
        </FormControl>

        <Button
          mt={6}
          colorScheme="blue"
          type="submit"
          isLoading={isSubmitting}
          loadingText="Submitting"
          width={{
            base: "100%",
            sm: "auto",
          }}
        >
          Submit Project
        </Button>
      </form>
    </Box>
  );
};

export default AddNewProject;